import { useState } from "react";
import { SupportLayout } from "@/components/support-layout";
import {
  Send,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
} from "lucide-react";

const API_URL =
  import.meta.env.VITE_BACKEND_API_URL || "https://api.nordex.trade";

const GOLD = "#C9A867";

const CATEGORIES = [
  { value: "", label: "Select a category..." },
  { value: "Bug Report", label: "Bug Report" },
  { value: "Feature Request", label: "Feature Request" },
  { value: "General Feedback", label: "General Feedback" },
  { value: "UI/UX", label: "UI/UX" },
];

export const Feedback = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "success" | "error"
  >("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const canSubmit =
    name.trim() && message.trim() && status !== "sending";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "feedback",
          name: name.trim(),
          email: email.trim() || "anonymous@anonymous.com",
          message: message.trim(),
          category: category || "General",
        }),
      });

      const data = await res.json();

      if (data.success) {
        setStatus("success");
        setName("");
        setEmail("");
        setCategory("");
        setMessage("");
      } else {
        setStatus("error");
        setErrorMsg(data.error || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please check your connection and try again.");
    }
  };

  return (
    <SupportLayout>
      {/* ── Intro Section ── */}
      <section className="mb-10">
        <h1 className="text-3xl font-bold mb-3 text-gray-900 dark:text-gray-100">
          Feedback
        </h1>
        <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
          Your feedback helps us improve Nordex. Share a bug report, feature
          request, or general feedback — we read every submission.
        </p>
      </section>

      {/* ── Feedback Form Card ── */}
      <section className="mb-12">
        <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 sm:px-8 py-5 sm:py-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Share your feedback
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
            Tell us what's working, what's not, or what you'd like to see next.
          </p>

          {status === "success" ? (
            <div className="py-8 text-center">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4"
                style={{ backgroundColor: `${GOLD}15` }}
              >
                <CheckCircle className="h-7 w-7" style={{ color: GOLD }} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Feedback Submitted!
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto">
                Thank you for helping us improve. We appreciate your input.
              </p>
              <button
                onClick={() => setStatus("idle")}
                className="text-sm font-medium hover:underline cursor-pointer"
                style={{ color: GOLD }}
              >
                Submit more feedback
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name + Email row */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <label
                    htmlFor="feedback-name"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="feedback-name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="feedback-email"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Email{" "}
                    <span className="text-gray-400 dark:text-gray-500 font-normal">
                      (optional, for follow-up)
                    </span>
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all"
                  />
                </div>
              </div>

              {/* Category dropdown */}
              <div className="space-y-1.5">
                <label
                  htmlFor="feedback-category"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Category
                </label>
                <div className="relative">
                  <select
                    id="feedback-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`w-full h-11 px-4 pr-10 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all cursor-pointer ${
                      category
                        ? "text-gray-900 dark:text-gray-100"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {/* Message */}
              <div className="space-y-1.5">
                <label
                  htmlFor="feedback-message"
                  className="text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  placeholder="Describe your feedback in detail..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 focus:border-gray-300 dark:focus:border-gray-600 transition-all resize-none"
                />
              </div>

              {/* Error */}
              {status === "error" && (
                <div className="flex items-center gap-2 text-sm text-red-500">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {errorMsg}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white h-11 px-8 transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 cursor-pointer"
                style={{ background: GOLD }}
              >
                {status === "sending" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </SupportLayout>
  );
};
